import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
  
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('#predictive-search') || this.querySelector('#predictive-search-modal');
      this.isOpen = false;

      // Bind handlers for proper cleanup
      this.boundClose = () => this.close();
      this.boundCloseWithoutEvent = () => this.closeWithoutEvent();
      this.boundHandlePredictiveSearchOpen = () => {
      // Only open modal if this is the modal search input, not inline search
      const searchModal = document.querySelector('[ref="searchModal"]');
      if (searchModal && this.input && this.input.getAttribute('ref') === 'searchModalInput') {
        this.openModal();
      }
      };
      this.boundHandleInput = this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this);
    }

    connectedCallback() {
      // Add event listeners
      ThemeBus.addEventListener(EVENTS.cartDrawerOpen, this.boundClose);
      ThemeBus.addEventListener(EVENTS.headerDrawerOpen, this.boundClose);
      ThemeBus.addEventListener(EVENTS.predictiveSearchClose, this.boundCloseWithoutEvent);
      ThemeBus.addEventListener(EVENTS.predictiveSearchOpen, this.boundHandlePredictiveSearchOpen);

      if (this.input) {
        this.input.addEventListener('input', this.boundHandleInput);
      }
    }

    disconnectedCallback() {
      // Remove event listeners
      ThemeBus.removeEventListener(EVENTS.cartDrawerOpen, this.boundClose);
      ThemeBus.removeEventListener(EVENTS.headerDrawerOpen, this.boundClose);
      ThemeBus.removeEventListener(EVENTS.predictiveSearchClose, this.boundCloseWithoutEvent);
      ThemeBus.removeEventListener(EVENTS.predictiveSearchOpen, this.boundHandlePredictiveSearchOpen);

      if (this.input) {
        this.input.removeEventListener('input', this.boundHandleInput);
      }

      // Clean up modal listeners if they exist
      this.cleanupModalListeners();
    }
  
    onChange() {
      const searchTerm = this.input.value.trim();
  
      if (!searchTerm.length) {
        this.close();
        return;
      }
  
      this.getSearchResults(searchTerm);
    }
  
    getSearchResults(searchTerm) {
      fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
  
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          this.predictiveSearchResults.innerHTML = resultsMarkup;

          // Dispatch gridUpdated event to trigger lazy loading (centralized in lazy-image.js)
          ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));

          this.open();
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }
  
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.predictiveSearchResults.style.display = 'block';
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
    }
  
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.predictiveSearchResults.style.display = 'none';

        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
        ThemeBus.dispatchEvent(new Event(EVENTS.predictiveSearchClose));
    }

    closeWithoutEvent() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.predictiveSearchResults.style.display = 'none';
      try { 
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
      } catch (_) {}
    }
  
    openModal() {
      const searchModal = document.querySelector('[ref="searchModal"]');
      const searchInput = document.querySelector('[ref="searchModalInput"]');
      const searchModalClose = document.querySelector('[ref="searchModalClose"]');
      const header = document.querySelector('header');

      // Store references for cleanup
      this.modalElements = {
        modal: searchModal,
        close: searchModalClose
      };

      // Bind handlers for cleanup
      this.boundCloseModal = () => this.closeModal();
      this.boundHandleOverlayClick = (e) => {
        if (e.target === searchModal) {
          this.closeModal();
        }
      };
      this.boundHandleEscapeKey = (e) => {
        if (e.key === 'Escape' && searchModal && !searchModal.classList.contains('hidden')) {
          this.closeModal();
        }
      };

      if (searchModal) {
        // Calculate header height and position modal below it
        if (header) {
          const headerHeight = header.offsetHeight;
          const modalDialog = searchModal.querySelector('[data-modal-dialog]');
          if (modalDialog) {
            modalDialog.style.marginTop = `${headerHeight + 50}px`;
          }
        }

        searchModal.classList.remove('hidden');
        searchModal.setAttribute('aria-hidden', 'false');

        // Lock body scroll
        document.body.style.overflow = 'hidden';

        // Close on button click
        if (searchModalClose) {
          searchModalClose.addEventListener('click', this.boundCloseModal);
        }

        // Close on overlay click
        searchModal.addEventListener('click', this.boundHandleOverlayClick);

        // Close on Escape key
        document.addEventListener('keydown', this.boundHandleEscapeKey);
      }

      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 100);
      }


        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
    }

    cleanupModalListeners() {
      // Remove modal event listeners
      if (this.modalElements) {
        if (this.modalElements.modal && this.boundHandleOverlayClick) {
          this.modalElements.modal.removeEventListener('click', this.boundHandleOverlayClick);
        }
        if (this.modalElements.close && this.boundCloseModal) {
          this.modalElements.close.removeEventListener('click', this.boundCloseModal);
        }
      }

      if (this.boundHandleEscapeKey) {
        document.removeEventListener('keydown', this.boundHandleEscapeKey);
      }

      // Clear references
      this.modalElements = null;
      this.boundCloseModal = null;
      this.boundHandleOverlayClick = null;
      this.boundHandleEscapeKey = null;
    }

    closeModal() {
      const searchModal = document.querySelector('[ref="searchModal"]');
      const searchInput = document.querySelector('[ref="searchModalInput"]');

      if (searchModal) {
        searchModal.classList.add('hidden');
        searchModal.setAttribute('aria-hidden', 'true');
      }

      if (searchInput) {
        searchInput.value = '';
      }

      // Unlock body scroll
      document.body.style.overflow = '';

      // Clean up modal listeners
      this.cleanupModalListeners();

      this.close();

        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);

// ========================================
// Search Input Clear Button Handler
// ========================================

// Store listener references for cleanup
const searchClearButtonListeners = new WeakMap();

function initSearchClearButtons() {
  const clearButtons = document.querySelectorAll('[data-search-clear]');

  clearButtons.forEach(button => {
    const inputId = button.getAttribute('data-search-clear');
    const input = document.getElementById(inputId);

    if (!input) return;

    // Skip if already initialized
    if (searchClearButtonListeners.has(button)) return;

    // Show/hide clear button based on input value
    const toggleClearButton = () => {
      if (input.value.trim().length > 0) {
        button.classList.remove('opacity-0', 'invisible');
        button.classList.add('opacity-100', 'visible');
      } else {
        button.classList.remove('opacity-100', 'visible');
        button.classList.add('opacity-0', 'invisible');
      }
    };

    // Handle clear button click
    const handleClearClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      input.value = '';
      input.focus();
      toggleClearButton();

      // Close predictive search if open
      const predictiveSearch = input.closest('predictive-search');
      if (predictiveSearch && typeof predictiveSearch.close === 'function') {
        predictiveSearch.close();
      }
    };

    // Add listeners
    button.addEventListener('click', handleClearClick);
    input.addEventListener('input', toggleClearButton);

    // Store references for potential cleanup
    searchClearButtonListeners.set(button, {
      handleClearClick,
      toggleClearButton,
      input
    });

    // Initial state
    toggleClearButton();
  });
}

// Cleanup function (call to remove all search clear button listeners)
function cleanupSearchClearButtons() {
  const clearButtons = document.querySelectorAll('[data-search-clear]');

  clearButtons.forEach(button => {
    const listeners = searchClearButtonListeners.get(button);
    if (listeners) {
      button.removeEventListener('click', listeners.handleClearClick);
      listeners.input.removeEventListener('input', listeners.toggleClearButton);
      searchClearButtonListeners.delete(button);
    }
  });
}

// Store handler references for module-level cleanup
const handleDOMContentLoadedForClear = () => {
  initSearchClearButtons();
};

const handleModalOpenForClear = () => {
  setTimeout(initSearchClearButtons, 100);
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', handleDOMContentLoadedForClear);
} else {
  initSearchClearButtons();
}

// Reinitialize after modal opens (in case modal wasn't in DOM initially)
ThemeBus.addEventListener(EVENTS.modalOpen, handleModalOpenForClear);

// Export cleanup function
export function cleanupSearchModule() {
  cleanupSearchClearButtons();
  ThemeBus.removeEventListener(EVENTS.modalOpen, handleModalOpenForClear);
  // Note: DOMContentLoaded listener is one-time only, no need to remove
}